/**
 * Math editor DevTools panel — Redux-DevTools-like state inspector.
 *
 * Floating bottom-right panel that the user toggles via the
 * `lilia.math.devtools=1` localStorage flag (set it once, persists
 * across sessions). It is **not** mounted by default — only when the
 * flag is on at mount time.
 *
 * Two columns:
 *   - State: live snapshot of the editor's reactive state (tokens,
 *     cursor, slotFocus, commonTheme, modals, latex, autosave).
 *   - Log:   the last ~200 entries from `logger.ts`'s ring buffer.
 *
 * The panel does not own state — it reads through a `state` prop
 * passed from the editor and subscribes to the logger. That keeps it
 * out of the React commit cycle for the rest of the app: only the
 * panel itself re-renders when its inputs change.
 *
 * Keyboard:
 *   - Backtick (`)    Show / hide the panel
 *   - ⌘\ / Ctrl+\     Same (Mac / Windows-Linux)
 *
 * Buttons:
 *   - Console: toggle the logger's console mirror.
 *   - Clear:   wipe the in-memory buffer.
 *   - Pause:   stop appending new log lines to the view (history
 *              continues to fill the buffer; resume to re-attach).
 *   - Copy:    copy the current state snapshot as JSON.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { clearLogs, isConsoleEnabled, log as devlog, setConsoleEnabled, snapshotLogs, subscribeLogs, type LogEntry, type LogScope } from "./logger";
import type { FocusPath, Token } from "./tokens";
import type { ThemeId } from "./commonThemes";
import { useSyncStatusStore } from "@/store/syncStatusStore";
import { useEditorStore } from "@/store/editorStore";
import { api } from "@/lib/api";

const STORAGE_FLAG = "lilia.math.devtools";

export interface DevToolsState {
  tokens: Token[];
  cursor: number;
  focusPath: FocusPath;
  commonTheme: ThemeId;
  libraryOpen: boolean;
  saveOpen: boolean;
  drawerTab: string;
  latex: string;
  /** Wall-clock ms of the last successful host save, or null. */
  lastSavedAt: number | null;
  /** Wall-clock ms of the last autosave tick (null = never). */
  lastAutosaveAt: number | null;
  /** Currently-selected autosave interval in seconds (0 = off). */
  autosaveSeconds: number;
}

interface DevToolsPanelProps {
  state: DevToolsState;
}

/** Read the localStorage flag once at module import. The panel only
 *  mounts if the flag is set — flipping the flag requires a page
 *  reload. (Keeps the panel from existing during normal user runs.) */
export function isDevToolsEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try { return window.localStorage.getItem(STORAGE_FLAG) === "1"; }
  catch { return false; }
}

interface CaptureSummary {
  id: string;
  refToken: string;
  source: string;
  note: string | null;
  createdAt: string;
}

export function DevToolsPanel({ state }: DevToolsPanelProps) {
  const [visible, setVisible] = useState(true);
  const [paused, setPaused] = useState(false);
  const [tab, setTab] = useState<"state" | "logs" | "captures">("state");
  const [entries, setEntries] = useState<LogEntry[]>(() => snapshotLogs());

  // "My captures" tab state — lazy-loaded the first time the tab is
  // opened, then re-fetched whenever a new capture lands.
  const [myCaptures, setMyCaptures] = useState<CaptureSummary[] | null>(null);
  const [capturesLoading, setCapturesLoading] = useState(false);
  const [capturesError, setCapturesError] = useState<string | null>(null);
  const loadMyCaptures = useCallback(async () => {
    setCapturesLoading(true);
    setCapturesError(null);
    try {
      const rows = await api.diagnostics.listMine({ limit: 50 });
      setMyCaptures(rows.map((r) => ({
        id: r.id, refToken: r.refToken, source: r.source,
        note: r.note, createdAt: r.createdAt,
      })));
    } catch (e) {
      setCapturesError(e instanceof Error ? e.message : String(e));
    } finally {
      setCapturesLoading(false);
    }
  }, []);
  // Subscribe to the document-level sync store + editor store so the
  // panel shows the same numbers the indicator chip uses. Pulling
  // these in here (not as props) keeps the math editor component
  // unaware of document context — the panel is the only consumer.
  const sync = useSyncStatusStore();
  const docId = useEditorStore((s) => s.documentId);
  const editVersion = useEditorStore((s) => s.editVersion);
  const isDirty = useEditorStore((s) => s.isDirty);
  const blockCount = useEditorStore((s) => s.blocks.length);

  // Capture-share modal state. The capture button bundles the
  // current state + the log buffer + browser info into a single
  // JSON payload, posts it to /api/diagnostics/captures, and shows
  // the returned short refToken so the user can paste it for
  // follow-up analysis.
  const [shareOpen, setShareOpen] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [shareNote, setShareNote] = useState("");
  const [shareResult, setShareResult] = useState<{ refToken: string; payloadJson: string } | null>(null);

  const buildPayload = useCallback(() => ({
    capturedAt: new Date().toISOString(),
    editor: {
      cursor: state.cursor,
      tokenCount: state.tokens.length,
      tokens: state.tokens,
      focusPath: state.focusPath,
      commonTheme: state.commonTheme,
      drawerTab: state.drawerTab,
      libraryOpen: state.libraryOpen,
      saveOpen: state.saveOpen,
      latex: state.latex,
      lastSavedAt: state.lastSavedAt,
      lastAutosaveAt: state.lastAutosaveAt,
      autosaveSeconds: state.autosaveSeconds,
    },
    sync: {
      state: sync.state,
      version: sync.version,
      pendingCount: sync.pendingCount,
      lastSyncedAt: sync.lastSyncedAt ? sync.lastSyncedAt.toISOString() : null,
      detail: sync.detail,
    },
    document: {
      docId,
      editVersion,
      isDirty,
      blockCount,
    },
    logs: snapshotLogs(),
  }), [state, sync, docId, editVersion, isDirty, blockCount]);

  const handleCapture = useCallback(async () => {
    setShareOpen(true);
    setShareError(null);
    setShareBusy(true);
    setShareResult(null);
    const payload = buildPayload();
    const payloadJson = safeJson(payload);
    try {
      const ua = typeof navigator !== "undefined" ? navigator.userAgent : null;
      const url = typeof window !== "undefined" ? window.location.href : null;
      devlog.info("state", "diagnostic capture submitted", { bytes: payloadJson.length });
      const res = await api.diagnostics.capture({
        source: "math-editor",
        note: shareNote.trim() || undefined,
        userAgent: ua ?? undefined,
        url: url ?? undefined,
        payload,
      });
      setShareResult({ refToken: res.refToken, payloadJson });
      devlog.info("state", "diagnostic capture stored", { refToken: res.refToken });
      // Refresh the "my captures" tab if we already loaded it once,
      // so the newest entry shows up without a manual refresh click.
      if (myCaptures !== null) void loadMyCaptures();
    } catch (e) {
      // POST failed — still show the JSON so the user can paste it
      // or download the file directly. The server-side store is the
      // happy path, not the only path.
      const message = e instanceof Error ? e.message : String(e);
      setShareError(message);
      setShareResult({ refToken: "(server reject — paste JSON below)", payloadJson });
      devlog.error("state", "diagnostic capture POST failed", { error: message });
    } finally {
      setShareBusy(false);
    }
  }, [buildPayload, shareNote, myCaptures, loadMyCaptures]);

  const handleDownload = useCallback(() => {
    if (!shareResult) return;
    const blob = new Blob([shareResult.payloadJson], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lilia-math-capture-${shareResult.refToken.replace(/[^a-z0-9-]/gi, "")}-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [shareResult]);

  const handleCopyJson = useCallback(() => {
    if (!shareResult) return;
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(shareResult.payloadJson).catch(() => {});
    }
  }, [shareResult]);

  const handleCopyToken = useCallback(() => {
    if (!shareResult) return;
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(shareResult.refToken).catch(() => {});
    }
  }, [shareResult]);
  const pauseRef = useRef(paused);
  pauseRef.current = paused;

  useEffect(() => {
    return subscribeLogs((_entry, buf) => {
      if (pauseRef.current) return;
      setEntries(buf);
    });
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Don't steal keystrokes when the user is typing in an input
      const t = e.target as Element | null;
      if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement) return;
      if (t && "isContentEditable" in t && (t as HTMLElement).isContentEditable) return;
      const meta = e.metaKey || e.ctrlKey;
      // Cmd+Shift+D — capture-immediately. Opens the share modal AND
      // fires the capture in a single step so the user doesn't have
      // to find the button. Works whether or not the panel is open.
      if (meta && e.shiftKey && (e.key === "d" || e.key === "D")) {
        e.preventDefault();
        setVisible(true);
        handleCapture();
        return;
      }
      // Backtick or Cmd+\\ — toggle panel visibility.
      if (e.key === "`" || (meta && e.key === "\\")) {
        e.preventDefault();
        setVisible((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleCapture]);

  const stateJson = useMemo(() => safeJson({
    cursor: state.cursor,
    tokenCount: state.tokens.length,
    focusPath: state.focusPath,
    commonTheme: state.commonTheme,
    drawerTab: state.drawerTab,
    libraryOpen: state.libraryOpen,
    saveOpen: state.saveOpen,
    latex: state.latex,
    lastSavedAt: state.lastSavedAt ? new Date(state.lastSavedAt).toISOString() : null,
    lastAutosaveAt: state.lastAutosaveAt ? new Date(state.lastAutosaveAt).toISOString() : null,
    autosaveSeconds: state.autosaveSeconds,
  }), [state]);

  const handleCopyState = useCallback(() => {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(stateJson).catch(() => {});
    }
  }, [stateJson]);

  const tokenList = useMemo(() => state.tokens.map((tok, i) => {
    const flag = i === state.cursor ? "▶ " : "  ";
    const sig = compactToken(tok);
    return `${String(i).padStart(2, " ")} ${flag}${sig}`;
  }).join("\n") || "(empty)", [state.tokens, state.cursor]);

  if (!visible) {
    return (
      <button
        type="button"
        onClick={() => setVisible(true)}
        title="Open math editor DevTools (`)"
        // z-index 950 sits ABOVE the editor surface (default stacking)
        // but BELOW the math editor's modals (Library/Save/Capture at
        // 1000) so an open modal isn't covered by the panel chip. The
        // chip stays visible when no modal is up.
        style={{
          position: "fixed", right: 12, bottom: 12, zIndex: 950,
          height: 26, padding: "0 9px",
          borderRadius: 999, border: 0,
          background: "var(--lilia-text)", color: "#fff",
          fontFamily: "ui-monospace, monospace",
          fontSize: 11, cursor: "pointer",
          boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
        }}
      >math DevTools</button>
    );
  }

  return (
    <div
      role="region"
      aria-label="Math editor DevTools"
      // Same layering note as the floating chip — keep the panel
      // below the math editor's modals so it never blocks pointer
      // events on Library / Save / Capture flows.
      style={{
        position: "fixed", right: 12, bottom: 12, zIndex: 950,
        width: 460,
        maxHeight: "60vh",
        background: "#15110f", color: "#e8e3d8",
        borderRadius: 10,
        boxShadow: "0 12px 36px rgba(0,0,0,0.45)",
        fontFamily: "ui-monospace, SFMono-Regular, Consolas, monospace",
        fontSize: 11,
        display: "flex", flexDirection: "column", overflow: "hidden",
      }}
    >
      <header style={{
        padding: "8px 10px",
        background: "#0f0c0a",
        display: "flex", alignItems: "center", gap: 8,
        borderBottom: "1px solid #2a221d",
      }}>
        <span style={{ color: "#c4b0ff", fontWeight: 700 }}>math DevTools</span>
        <span style={{ flex: 1 }} />
        <Btn onClick={() => setTab("state")} active={tab === "state"}>state</Btn>
        <Btn onClick={() => setTab("logs")}  active={tab === "logs"}>logs ({entries.length})</Btn>
        <Btn
          onClick={() => {
            setTab("captures");
            if (myCaptures === null) void loadMyCaptures();
          }}
          active={tab === "captures"}
          title="My recent captures"
        >mine{myCaptures !== null ? ` (${myCaptures.length})` : ""}</Btn>
        <Btn onClick={handleCapture} title="Capture state + logs for analysis (Cmd+Shift+D)">📋 capture</Btn>
        <Btn onClick={() => setVisible(false)} title="Close (`)">×</Btn>
      </header>

      {tab === "state" ? (
        <div style={{
          padding: 10, overflow: "auto",
          flex: 1, minHeight: 0,
        }}>
          {/* Host sync — pulled from the document-level stores. The
              indicator chip in the top bar uses the same numbers. A
              409 leaves a 'conflict' state here + a 'rebase' entry
              in the logs tab; this is the fastest way to fingerprint
              the symptom from the user's screenshot. */}
          <SectionTitle>host sync</SectionTitle>
          <Row label="docId">{docId ?? "(none)"}</Row>
          <Row label="syncState">
            <span style={{ color: syncStateColor(sync.state), fontWeight: 700 }}>{sync.state}</span>
            {sync.detail && <span style={{ marginLeft: 8, color: "#a8a195" }}>· {sync.detail}</span>}
          </Row>
          <Row label="serverVersion">{sync.version}</Row>
          <Row label="editVersion">{editVersion} {isDirty && <span style={{ color: "#fbbf24" }}>(dirty)</span>}</Row>
          <Row label="pendingCount">{sync.pendingCount}</Row>
          <Row label="lastSyncedAt">{sync.lastSyncedAt ? formatTs(sync.lastSyncedAt.getTime()) : "never"}</Row>
          <Row label="blocks">{blockCount}</Row>

          <SectionTitle style={{ marginTop: 14 }}>editor state</SectionTitle>
          <Row label="cursor">{state.cursor} of {state.tokens.length}</Row>
          <Row label="focusPath">
            {state.focusPath.length
              ? state.focusPath.map((s) => `${s.tokenIdx}·${s.slotKey}`).join(" › ")
              : "root"}
          </Row>
          <Row label="commonTheme">{state.commonTheme}</Row>
          <Row label="drawerTab">{state.drawerTab}</Row>
          <Row label="libraryOpen">{String(state.libraryOpen)}</Row>
          <Row label="saveOpen">{String(state.saveOpen)}</Row>
          <Row label="autosaveSeconds">{state.autosaveSeconds === 0 ? "off" : `${state.autosaveSeconds}s`}</Row>
          <Row label="lastSavedAt">{formatTs(state.lastSavedAt)}</Row>
          <Row label="lastAutosaveAt">{formatTs(state.lastAutosaveAt)}</Row>
          <Row label="latex">
            <pre style={{
              margin: "4px 0 0", padding: "6px 8px",
              background: "#0a0807", border: "1px solid #2a221d",
              borderRadius: 4, whiteSpace: "pre-wrap", wordBreak: "break-word",
              maxHeight: 110, overflow: "auto",
            }}>{state.latex || "(empty)"}</pre>
          </Row>
          <Row label="tokens">
            <pre style={{
              margin: "4px 0 0", padding: "6px 8px",
              background: "#0a0807", border: "1px solid #2a221d",
              borderRadius: 4, whiteSpace: "pre", overflow: "auto",
              maxHeight: 200,
            }}>{tokenList}</pre>
          </Row>
          <div style={{ marginTop: 10, display: "flex", gap: 6 }}>
            <Btn onClick={handleCopyState}>copy state</Btn>
          </div>
        </div>
      ) : tab === "logs" ? (
        <LogStream
          entries={entries}
          paused={paused}
          onPause={() => setPaused((p) => !p)}
          onClear={() => clearLogs()}
        />
      ) : (
        <CapturesList
          rows={myCaptures}
          loading={capturesLoading}
          error={capturesError}
          onReload={loadMyCaptures}
        />
      )}

      {shareOpen && (
        <ShareModal
          busy={shareBusy}
          error={shareError}
          note={shareNote}
          setNote={setShareNote}
          result={shareResult}
          onClose={() => setShareOpen(false)}
          onCapture={handleCapture}
          onDownload={handleDownload}
          onCopyJson={handleCopyJson}
          onCopyToken={handleCopyToken}
        />
      )}
    </div>
  );
}

function CapturesList({
  rows, loading, error, onReload,
}: {
  rows: CaptureSummary[] | null;
  loading: boolean;
  error: string | null;
  onReload: () => void;
}) {
  const copyToken = useCallback((token: string) => {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(token).catch(() => {});
    }
  }, []);
  const copyLink = useCallback((token: string) => {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      // Link to the viewer page (the readable surface) rather than
      // the raw API endpoint. The API URL is still useful for the
      // analyst when scripting; flip the constant if a future
      // workflow needs it back.
      navigator.clipboard
        .writeText(`${origin}/diagnostics/${token}`)
        .catch(() => {});
    }
  }, []);
  const openInViewer = useCallback((token: string) => {
    if (typeof window !== "undefined") {
      window.open(`/diagnostics/${token}`, "_blank", "noopener,noreferrer");
    }
  }, []);

  return (
    <>
      <div style={{
        padding: "6px 10px", display: "flex", alignItems: "center", gap: 6,
        borderBottom: "1px solid #2a221d", background: "#0f0c0a",
      }}>
        <span style={{ color: "#a8a195" }}>
          {rows == null
            ? "Loading…"
            : `${rows.length} capture${rows.length === 1 ? "" : "s"}`}
        </span>
        <span style={{ flex: 1 }} />
        <Btn onClick={onReload}>refresh</Btn>
      </div>
      <div style={{ padding: 8, overflow: "auto", flex: 1, minHeight: 0 }}>
        {loading && rows == null && (
          <p style={{ color: "#a8a195", fontStyle: "italic" }}>Fetching…</p>
        )}
        {error && (
          <p style={{ color: "#ff8d8d" }}>{error}</p>
        )}
        {rows != null && rows.length === 0 && !loading && (
          <p style={{ color: "#a8a195", fontStyle: "italic" }}>
            No captures yet. Hit 📋 capture to make one.
          </p>
        )}
        {rows?.map((c) => (
          <div key={c.id} style={{
            marginBottom: 6, padding: "8px 10px",
            background: "#0a0807", border: "1px solid #221c18",
            borderRadius: 4,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <code style={{ color: "#7ed3a4", fontWeight: 700 }}>{c.refToken}</code>
              <span style={{ color: "#8a8074", fontSize: 10 }}>
                {formatHmsMs(new Date(c.createdAt).getTime())}
              </span>
              <span style={{ flex: 1 }} />
              <Btn onClick={() => openInViewer(c.refToken)}>open ↗</Btn>
              <Btn onClick={() => copyToken(c.refToken)}>copy token</Btn>
              <Btn onClick={() => copyLink(c.refToken)}>copy link</Btn>
            </div>
            <div style={{ color: "#a8a195", marginTop: 4 }}>
              <span style={{ color: "#c4b0ff" }}>{c.source}</span>
              {c.note && <> · {c.note}</>}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function ShareModal({
  busy, error, note, setNote, result,
  onClose, onCapture, onDownload, onCopyJson, onCopyToken,
}: {
  busy: boolean;
  error: string | null;
  note: string;
  setNote: (s: string) => void;
  result: { refToken: string; payloadJson: string } | null;
  onClose: () => void;
  onCapture: () => void;
  onDownload: () => void;
  onCopyJson: () => void;
  onCopyToken: () => void;
}) {
  return (
    <div
      role="dialog" aria-modal="true" aria-label="Share diagnostic capture"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 100000,
        background: "rgba(8,6,5,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}
    >
      <div style={{
        width: "min(640px, 100%)",
        maxHeight: "min(80vh, 720px)",
        background: "#15110f", color: "#e8e3d8",
        borderRadius: 10,
        boxShadow: "0 16px 48px rgba(0,0,0,0.55)",
        display: "flex", flexDirection: "column", overflow: "hidden",
        fontFamily: "ui-monospace, monospace", fontSize: 12,
      }}>
        <header style={{
          padding: "10px 14px", borderBottom: "1px solid #2a221d",
          display: "flex", alignItems: "center", gap: 8,
          background: "#0f0c0a",
        }}>
          <span style={{ color: "#c4b0ff", fontWeight: 700 }}>capture for analysis</span>
          <span style={{ flex: 1 }} />
          <Btn onClick={onClose}>close</Btn>
        </header>

        <div style={{ padding: 14, overflow: "auto", flex: 1, minHeight: 0 }}>
          {!result && !busy && (
            <p style={{ color: "#a8a195", marginTop: 0 }}>
              Click <strong>capture</strong> to bundle the current editor state,
              host sync, browser info, and the last ~300 log entries — then upload
              to the server. You'll get a short ref token to share.
            </p>
          )}

          <label style={{ display: "block", marginTop: 4 }}>
            <span style={{ color: "#a8a195" }}>What were you doing? (optional)</span>
            <input
              type="text" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder='e.g. "loaded Taylor series, hit Save, got 409"'
              disabled={busy}
              style={{
                display: "block", width: "100%", marginTop: 4,
                background: "#0a0807", color: "#e8e3d8",
                border: "1px solid #2a221d", borderRadius: 4,
                padding: "6px 8px", fontFamily: "inherit", fontSize: 12,
              }}
            />
          </label>

          {busy && (
            <p style={{ color: "#9ec5ff", marginTop: 10 }}>Posting capture…</p>
          )}

          {error && !result?.refToken.startsWith("cap-") && (
            <p style={{
              marginTop: 10, padding: "8px 10px",
              background: "rgba(255,141,141,0.10)", border: "1px solid #4a1a1a",
              borderRadius: 4, color: "#ff8d8d",
            }}>
              Server-side store failed: {error}<br />
              The JSON is still available below — copy / download and paste it manually.
            </p>
          )}

          {result && (
            <div style={{ marginTop: 12 }}>
              <div style={{
                padding: "10px 12px",
                background: "#0a0807", border: "1px solid #2a221d",
                borderRadius: 4, marginBottom: 10,
              }}>
                <span style={{ color: "#a8a195" }}>ref token: </span>
                <code style={{
                  color: "#7ed3a4", fontSize: 14, fontWeight: 700,
                  letterSpacing: 0.5,
                }}>{result.refToken}</code>
                <span style={{ flex: 1 }} />
                <Btn onClick={onCopyToken}>copy token</Btn>
              </div>

              <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                <Btn onClick={onCopyJson}>copy JSON</Btn>
                <Btn onClick={onDownload}>download .json</Btn>
              </div>

              <details style={{ marginTop: 8 }}>
                <summary style={{ cursor: "pointer", color: "#a8a195" }}>
                  preview payload ({Math.round(result.payloadJson.length / 1024)} KB)
                </summary>
                <pre style={{
                  marginTop: 8, padding: "10px 12px",
                  background: "#0a0807", border: "1px solid #2a221d",
                  borderRadius: 4, whiteSpace: "pre-wrap", wordBreak: "break-word",
                  maxHeight: 360, overflow: "auto",
                  fontSize: 11, color: "#a8a195",
                }}>{result.payloadJson}</pre>
              </details>
            </div>
          )}
        </div>

        {!result && (
          <footer style={{
            padding: "10px 14px", borderTop: "1px solid #2a221d",
            display: "flex", justifyContent: "flex-end", gap: 6,
            background: "#0f0c0a",
          }}>
            <Btn onClick={onClose}>cancel</Btn>
            <Btn onClick={onCapture} active>capture</Btn>
          </footer>
        )}
      </div>
    </div>
  );
}

function LogStream({
  entries, paused, onPause, onClear,
}: {
  entries: LogEntry[]; paused: boolean;
  onPause: () => void; onClear: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [filter, setFilter] = useState<LogScope | "all">("all");
  const [consoleOn, setConsoleOn] = useState<boolean>(() => isConsoleEnabled());

  useEffect(() => {
    if (!paused && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [entries, paused]);

  const filtered = useMemo(
    () => filter === "all" ? entries : entries.filter((e) => e.scope === filter),
    [entries, filter],
  );

  return (
    <>
      <div style={{
        padding: "6px 10px", display: "flex", alignItems: "center", gap: 6,
        borderBottom: "1px solid #2a221d", background: "#0f0c0a",
      }}>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as LogScope | "all")}
          style={{
            background: "#1a1410", color: "#e8e3d8",
            border: "1px solid #2a221d", borderRadius: 4,
            fontFamily: "inherit", fontSize: 11,
            padding: "2px 6px",
          }}
        >
          <option value="all">all scopes</option>
          {SCOPES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <span style={{ flex: 1 }} />
        <Btn onClick={() => { const next = !consoleOn; setConsoleEnabled(next); setConsoleOn(next); }}
             active={consoleOn} title="Toggle window.console mirror">console</Btn>
        <Btn onClick={onPause} active={paused}>{paused ? "resume" : "pause"}</Btn>
        <Btn onClick={onClear}>clear</Btn>
      </div>
      <div ref={containerRef} style={{
        padding: 8, overflow: "auto", flex: 1, minHeight: 0,
        fontVariantNumeric: "tabular-nums",
      }}>
        {filtered.length === 0 ? (
          <p style={{ color: "#8a8074", fontStyle: "italic" }}>
            No entries yet. Use the editor — logs land here as state changes.
          </p>
        ) : (
          filtered.map((e) => (
            <div key={e.seq} style={{ marginBottom: 5 }}>
              <span style={{ color: "#8a8074" }}>{formatHmsMs(e.ts)} </span>
              <span style={{ color: levelColor(e.level), fontWeight: 600 }}>{e.level.padEnd(5, " ")}</span>
              <span style={{ color: "#c4b0ff", marginLeft: 4 }}>{e.scope}</span>
              <span style={{ marginLeft: 6 }}>{e.message}</span>
              {e.data !== undefined && (
                <pre style={{
                  margin: "2px 0 0 12px", padding: "4px 6px",
                  background: "#0a0807", border: "1px solid #221c18",
                  borderRadius: 3, color: "#a8a195",
                  whiteSpace: "pre-wrap", wordBreak: "break-word",
                  maxHeight: 90, overflow: "auto",
                }}>{safeJson(e.data)}</pre>
              )}
            </div>
          ))
        )}
      </div>
    </>
  );
}

const SCOPES: LogScope[] = [
  "init", "state", "insert", "backspace", "caret", "slot",
  "save", "autosave", "load", "library", "done", "host",
  "sync", "sync-tick", "rebase",
];

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 10, padding: "3px 0", borderBottom: "1px dotted #2a221d", flexWrap: "wrap" }}>
      <span style={{ color: "#a8a195", minWidth: 110, fontWeight: 600 }}>{label}</span>
      <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
    </div>
  );
}

function SectionTitle({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{
      fontSize: 9.5, letterSpacing: "0.12em", textTransform: "uppercase",
      color: "#c4b0ff", fontWeight: 700,
      padding: "0 0 4px",
      borderBottom: "1px solid #2a221d",
      marginBottom: 4,
      ...style,
    }}>{children}</div>
  );
}

function syncStateColor(s: string): string {
  switch (s) {
    case "upToDate": return "#7ed3a4";
    case "syncing":  return "#9ec5ff";
    case "offline":  return "#a8a195";
    case "error":    return "#ff8d8d";
    // Kept after the live state was removed (14 Sep 2026): this renders
    // historical telemetry, which can still carry the old value.
    case "conflict": return "#fbbf24";
    default:         return "#e8e3d8";
  }
}

function Btn({
  onClick, children, active, title,
}: {
  onClick?: () => void; children: React.ReactNode; active?: boolean; title?: string;
}) {
  return (
    <button
      type="button" onClick={onClick} title={title}
      style={{
        padding: "2px 8px",
        borderRadius: 4,
        border: 0,
        background: active ? "var(--lilia-primary)" : "var(--lilia-text)",
        color: active ? "#0e0e10" : "#e8e3d8",
        fontFamily: "inherit", fontSize: 11, fontWeight: 600,
        cursor: "pointer",
      }}
    >{children}</button>
  );
}

function compactToken(t: Token): string {
  const k = t.kind;
  if (k === "struct") {
    const s = t as Token & { structKind: string; slots: Record<string, string>; slotOrder: string[] };
    const slotPreview = s.slotOrder
      .map((key) => `${key}=${JSON.stringify(s.slots?.[key] ?? "")}`)
      .join(" ");
    return `struct ${s.structKind} { ${slotPreview} }`;
  }
  const g = (t as { glyph?: string }).glyph;
  return `${k}${g ? ` "${g}"` : ""}`;
}

function levelColor(level: string): string {
  switch (level) {
    case "error": return "#ff8d8d";
    case "warn":  return "#fbbf24";
    case "debug": return "#9ec5ff";
    default:      return "#7ed3a4";
  }
}

function formatHmsMs(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number, w = 2) => String(n).padStart(w, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
}

function formatTs(ts: number | null): string {
  if (!ts) return "never";
  return `${formatHmsMs(ts)} (${Math.round((Date.now() - ts) / 1000)}s ago)`;
}

function safeJson(v: unknown): string {
  try { return JSON.stringify(v, null, 2); }
  catch { return String(v); }
}
